import {
  Anchor,
  Button,
  ButtonGroup,
  Heading,
  List,
  Modal,
  Paragraph,
  TextButton,
  useToast,
} from '@zillow/constellation-10';
import { useEffectOnce } from 'react-use';
import { EXTERNAL_URLS, FAIR_HOUSING_URL } from '../../constants/urls';
import type { UserStatus } from '../../graphql/queries';
import { getDaysLeftOnPayment } from '../../utils/user-statuses';
import { pluralize } from '../../utils/formatters';
import type { SubmittedApplication, SubmittedOrSharedApplication } from '../../contexts/BaseContext';
import { SUBMISSION_TYPES } from '../../contexts/BaseContext';
import { usePaymentRequestTreatment, usePurchaseExpectationsTreatment } from '../../utils/ab-tests';
import { useState } from 'react';
import { PaymentRequestToast, PaymentsAndReportsUpsellBox } from './Sections/6Reports/1PaymentUpsellTip';
import {
  emitPaymentUpsellConfirmModalImpressionEvent,
  emitPaymentUpsellConfirmModalInteractionEvent,
  emitPostSubmitApplyToMorePropertiesCtaEvent,
  emitPostSubmitParticipatingPropertiesLinkEvent,
} from '../../analytics/clickstreamEvents';
import { useOptInOutRenterRequestPayment, useRenterRequestedZillowPayments } from '../../utils/rentPaymentQueries';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

type Props = {
  onClose: () => void;
  userStatuses: Array<UserStatus>;
  submittedApplication: SubmittedOrSharedApplication;
};

function isSubmittedApplication(app: SubmittedOrSharedApplication): app is SubmittedApplication {
  return app.type === SUBMISSION_TYPES.SUBMITTED;
}

function getSearchResultsPageUrl(city?: string | null, state?: string | null): string {
  if (city && state) {
    // Format: /city-state/rentals/ (e.g., /austin-tx/rentals/)
    const formattedCity = city.toLowerCase().replace(/\s+/g, '-');
    const formattedState = state.toLowerCase();

    // Query params needed for enabling "accepts zillow applications filter"
    const queryParams = {
      filterState: {
        fr: { value: true },
        fsba: { value: false },
        fsbo: { value: false },
        nc: { value: false },
        cmsn: { value: false },
        auc: { value: false },
        fore: { value: false },
        mf: { value: false },
        land: { value: false },
        manu: { value: false },
        app: { value: true },
      },
    };

    return `/${formattedCity}-${formattedState}/rentals/?searchQueryState=${encodeURIComponent(JSON.stringify(queryParams))}`;
  }
  return EXTERNAL_URLS.RENTALS_SEARCH_PAGE_URL;
}

export function ApplicationSubmitted({ onClose, userStatuses, submittedApplication }: Props) {
  const applicationContext = useApplicationContextOrUndefined();
  const daysLeftOnPayment = getDaysLeftOnPayment(userStatuses);
  const paymentRequestTreatment = usePaymentRequestTreatment();
  const purchaseExpectationsTreatment = usePurchaseExpectationsTreatment();
  const isSubmitted = isSubmittedApplication(submittedApplication);
  const requestedZillowPaymentsOriginal = useRenterRequestedZillowPayments(
    isSubmitted ? submittedApplication.applicationId : '',
    {
      enabled: isSubmitted && paymentRequestTreatment != null && paymentRequestTreatment !== 'off',
    },
  );
  const [requestedZillowPaymentsNow, setRequestedZillowPaymentsNow] = useState<boolean | null>(null);
  const optInOutPayments = useOptInOutRenterRequestPayment(
    isSubmitted ? submittedApplication.applicationId : '',
    isSubmitted ? submittedApplication.listingAlias : null,
  );

  const requestedZillowPayments =
    requestedZillowPaymentsNow != null ? requestedZillowPaymentsNow : requestedZillowPaymentsOriginal;
  const showUpsell = paymentRequestTreatment === 'on_3' && requestedZillowPayments == null;

  const { enqueueToast } = useToast();

  const handleOptOutPayments = async () => {
    await optInOutPayments(false);
    await setRequestedZillowPaymentsNow(false);
  };
  const handleOptInPayments = async () => {
    await optInOutPayments(true);
    await setRequestedZillowPaymentsNow(true);
    enqueueToast(<PaymentRequestToast onUndoRequestPayments={handleOptOutPayments} />);
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      body={
        <>
          <Heading level={1} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            Congrats! You {submittedApplication.type === SUBMISSION_TYPES.SUBMITTED ? `submitted` : `shared`} your
            application
          </Heading>
          <Heading level={2} textStyle="body-lg-bold">
            Next steps
          </Heading>
          <Paragraph>
            <List.Root appearance="bullet" css={{ marginBottom: 'tighter' }}>
              <List.Item>
                The rental manager will take it from here. You can reach out to them about next steps.
              </List.Item>
              {daysLeftOnPayment > 0 && (
                <List.Item>
                  You can reuse this application at{' '}
                  {purchaseExpectationsTreatment === 'on' ? (
                    <Anchor
                      href={getSearchResultsPageUrl(
                        submittedApplication.address.city,
                        submittedApplication.address.state,
                      )}
                      onClick={() => emitPostSubmitParticipatingPropertiesLinkEvent(applicationContext)}
                    >
                      participating properties
                    </Anchor>
                  ) : (
                    'participating properties'
                  )}{' '}
                  for the next {pluralize('day', daysLeftOnPayment)}.
                </List.Item>
              )}
              {requestedZillowPayments && (
                <List.Item>
                  We’ll send a request for Zillow payments to your landlord if your application is accepted.
                </List.Item>
              )}
            </List.Root>
          </Paragraph>
          {showUpsell && (
            <PaymentsAndReportsUpsellBox
              sx={{
                marginTop: '$spacing.sm',
                marginBottom: '$spacing.sm',
              }}
            />
          )}
          <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
            Zillow is committed to fair, inclusive housing. Know and exercise your{' '}
            <Anchor href={FAIR_HOUSING_URL} target="_blank" rel="noopener noreferrer">
              {' '}
              fair housing rights
            </Anchor>
            .
          </Paragraph>
        </>
      }
      footer={
        showUpsell ? (
          <ButtonGroup aria-label="Request Zillow payments actions">
            <TextButton type="button" onClick={() => handleOptOutPayments}>
              Not now
            </TextButton>
            <RequestZillowPaymentsButton onClick={handleOptInPayments} />
          </ButtonGroup>
        ) : purchaseExpectationsTreatment === 'on' ? (
          <Button
            tone="brand"
            emphasis="filled"
            asChild
            onClick={() => emitPostSubmitApplyToMorePropertiesCtaEvent(applicationContext)}
          >
            <a href={getSearchResultsPageUrl(submittedApplication.address.city, submittedApplication.address.state)}>
              Apply to more properties
            </a>
          </Button>
        ) : (
          <Button tone="brand" emphasis="filled" onClick={onClose}>
            Go to your dashboard
          </Button>
        )
      }
    />
  );
}

export function RequestZillowPaymentsButton({ onClick }: { onClick: () => void }) {
  const applicationContext = useApplicationContextOrUndefined();

  useEffectOnce(() => {
    emitPaymentUpsellConfirmModalImpressionEvent(applicationContext);
  });

  const handleClick = () => {
    emitPaymentUpsellConfirmModalInteractionEvent(applicationContext);
    onClick();
  };

  return (
    <Button tone="brand" emphasis="filled" onClick={handleClick}>
      Request Zillow payments
    </Button>
  );
}
